import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import BaseSelect from '@/components/@IntranetLibrary/Select';
import CloseIcon from '@/public/icons/close_black.svg';
import ClockIcon from '@/public/icons/mypage/WorkHours.svg';
import { useAtomValue } from 'jotai';
import { fetchApi } from '@/utils/api/customAxios';
import { useSnackbar } from '@/hooks/global-tools';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { MyInfoState } from '@/recoil/atom';
import {
  durationToMinutes,
  formatLeaveDays,
  formatLeaveMinutes,
  LeaveDuration,
} from '@/utils/leave';
import LeaveCalendar from './LeaveCalendar';
import {
  approvalDialogActionsSx,
  approvalDialogPaperSx,
  getApprovalDialogActionButtonSx,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/approvalDialogStyles';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

/** 연차 summary API(/me/annual-leave/summary) 응답 — 부여/사용/전년 초과/잔여 */
type AnnualLeaveSummary = {
  granted: number;
  grantedDuration: LeaveDuration;
  used: number;
  usedDuration: LeaveDuration;
  overage: number;
  overageDuration: LeaveDuration;
  remaining: number;
  remainingDuration: LeaveDuration;
};

/** 연차 신청 단위 (시간차 = 시간 단위 사용) */
type LeaveUnit = 'DAY' | 'AM_HALF' | 'PM_HALF' | 'HOUR';

const UNIT_TABS: { unit: LeaveUnit; label: string }[] = [
  { unit: 'DAY', label: '하루 종일' },
  { unit: 'AM_HALF', label: 'MYPAGE.오전 반차' },
  { unit: 'PM_HALF', label: 'MYPAGE.오후 반차' },
  { unit: 'HOUR', label: '시간차' },
];

// 기본 근무시간 (myInfo.workingHours 미지정 시 fallback)
const DEFAULT_WORKING_HOURS = '09:00~18:00';
// 점심시간 12:00~13:00 (반차 4시간 환산 시 제외)
const LUNCH_START_MIN = 12 * 60;
const LUNCH_END_MIN = 13 * 60;
const HALF_WORK_MIN = 4 * 60; // 반차 = 4 근무시간

const POINT = 'var(--dk-accent, #0066CC)';

const timeToMin = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};
const minToTime = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(
    2,
    '0',
  )}`;
/** 'HH:MM' → 'HHMM' (API startHour/endHour 포맷) */
const toHHMM = (time: string): string => time.replace(':', '');

/** 출근시각에서 근무 4시간(점심 제외)을 더한 오전/오후 반차 분기 시각 */
const addWorkMinutes = (startMin: number, durMin: number): number => {
  let t = startMin;
  let remaining = durMin;
  while (remaining > 0) {
    if (t < LUNCH_START_MIN) {
      const avail = LUNCH_START_MIN - t;
      if (remaining <= avail) return t + remaining;
      remaining -= avail;
      t = LUNCH_END_MIN; // 점심시간 건너뜀
    } else {
      if (t < LUNCH_END_MIN) t = LUNCH_END_MIN;
      return t + remaining;
    }
  }
  return t;
};

const toOptions = (times: string[]) =>
  times.map((t) => ({ label: t, value: t }));

const formatDateLabel = (date: string, lang: string): string =>
  `${date} (${new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(
    dayjs(date).toDate(),
  )})`;

const AnnualLeaveModal = ({ open, onClose, onSuccess }: Props) => {
  const { t, i18n } = useTranslation();
  const unitTabs = useMemo(
    () => UNIT_TABS.map((tab) => ({ ...tab, label: t(tab.label) })),
    [t],
  );
  const { isMobilePath } = usePlatform();
  const actionButtonSx = getApprovalDialogActionButtonSx(isMobilePath);
  const { showSuccess, showError } = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);

  // 신청자 실제 근무시간 반영 (예: '08:00~17:00'). 미지정 시 기본 09:00~18:00.
  // dayStart/dayEnd: 전일 구간, halfBoundary: 오전/오후 반차 분기 시각(근무 4h),
  // hourOptions: 시간차 선택 옵션(근무시간 범위, 1시간 단위).
  const { dayStart, dayEnd, halfBoundary, hourOptions } = useMemo(() => {
    const [s, e] = (myInfo.workingHours || DEFAULT_WORKING_HOURS).split('~');
    const startStr = s || '09:00';
    const endStr = e || '18:00';
    const startMin = timeToMin(startStr);
    const endMin = timeToMin(endStr);
    const opts: string[] = [];
    for (let t = startMin; t <= endMin; t += 60) opts.push(minToTime(t));
    return {
      dayStart: startStr,
      dayEnd: endStr,
      halfBoundary: minToTime(addWorkMinutes(startMin, HALF_WORK_MIN)),
      hourOptions: opts,
    };
  }, [myInfo.workingHours]);

  // 휴가 사용 현황(부여/사용/전년 초과 사용/잔여휴가) — 연차 summary API 사용.
  // 모달 오픈 시마다 재조회하므로 신청/취소 후 재오픈 시 최신값이 반영된다.
  const [summary, setSummary] = useState<AnnualLeaveSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  useEffect(() => {
    if (!open) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    fetchApi<{ data: AnnualLeaveSummary }>({
      method: 'GET',
      url: '/api/v1/passport/me/annual-leave/summary',
    })
      .then((res) => setSummary(res?.data ?? null))
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [open]);

  const today = dayjs().format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  // 시작일 시간차: 시작 시각(편집) / 종료일 시간차: 종료 시각(편집)
  // (모달 오픈 시 dayStart/dayEnd 로 초기화됨)
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [startUnit, setStartUnit] = useState<LeaveUnit>('DAY');
  const [endUnit, setEndUnit] = useState<LeaveUnit>('DAY');
  const [isLoading, setIsLoading] = useState(false);
  // 사용 일수: API(/annual-leave/usage)로 계산. 1일 = 8시간.
  const [usedDays, setUsedDays] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);

  const isMultiDay = startDate !== endDate;

  // 모달 오픈 시 초기화
  useEffect(() => {
    if (!open) return;
    setStartDate(today);
    setEndDate(today);
    setStartTime(dayStart);
    setEndTime(dayEnd);
    setStartUnit('DAY');
    setEndUnit('DAY');
  }, [open, today, dayStart, dayEnd]);

  // 타입(연차/오전반차/오후반차)과 사용 시간을 양방향으로 동기화 (PORTAL-13429)
  // - 타입 선택 → 해당 구간 시각을 사용 시간에 채워 노출
  // - 사용 시간 변경 → 구간과 일치하는 타입으로 전환, 어느 것과도 다르면 '시간차'
  const unitToRange = (unit: LeaveUnit): [string, string] | null => {
    if (unit === 'DAY') return [dayStart, dayEnd];
    if (unit === 'AM_HALF') return [dayStart, halfBoundary];
    if (unit === 'PM_HALF') return [halfBoundary, dayEnd];
    return null; // HOUR: 사용자가 고른 시각 유지
  };
  const rangeToUnit = (begin: string, end: string): LeaveUnit => {
    if (begin === dayStart && end === dayEnd) return 'DAY';
    if (begin === dayStart && end === halfBoundary) return 'AM_HALF';
    if (begin === halfBoundary && end === dayEnd) return 'PM_HALF';
    return 'HOUR';
  };

  // 단일일: 타입 탭 선택
  const handleSingleUnitChange = (unit: LeaveUnit) => {
    setStartUnit(unit);
    const range = unitToRange(unit);
    if (range) {
      setStartTime(range[0]);
      setEndTime(range[1]);
    }
  };
  // 단일일: 사용 시간 변경
  const handleSingleBeginChange = (v: string) => {
    let nextEnd = endTime;
    if (timeToMin(endTime) <= timeToMin(v)) {
      nextEnd = minToTime(timeToMin(v) + 60);
      setEndTime(nextEnd);
    }
    setStartTime(v);
    setStartUnit(rangeToUnit(v, nextEnd));
  };
  const handleSingleEndChange = (v: string) => {
    setEndTime(v);
    setStartUnit(rangeToUnit(startTime, v));
  };

  // 다중일 시작일: 시작 시각만 편집 (종료는 퇴근시각 고정)
  const handleMultiStartUnitChange = (unit: LeaveUnit) => {
    setStartUnit(unit);
    const range = unitToRange(unit);
    if (range) setStartTime(range[0]);
  };
  const handleMultiBeginChange = (v: string) => {
    setStartTime(v);
    setStartUnit(rangeToUnit(v, dayEnd));
  };
  // 다중일 종료일: 종료 시각만 편집 (시작은 출근시각 고정)
  const handleMultiEndUnitChange = (unit: LeaveUnit) => {
    setEndUnit(unit);
    const range = unitToRange(unit);
    if (range) setEndTime(range[1]);
  };
  const handleMultiEndChange = (v: string) => {
    setEndTime(v);
    setEndUnit(rangeToUnit(dayStart, v));
  };

  // 다중일 전환 시 다중일에서 불가한 단위 보정
  // (시작일 오전 반차 불가 / 종료일 오후 반차 불가) + 타입에 맞춰 시각 재동기화
  useEffect(() => {
    if (!isMultiDay) return;
    if (startUnit === 'AM_HALF') setStartUnit('DAY');
    if (endUnit === 'PM_HALF') setEndUnit('DAY');
  }, [isMultiDay, startUnit, endUnit]);
  useEffect(() => {
    if (isMultiDay) {
      const s = unitToRange(startUnit);
      const e = unitToRange(endUnit);
      if (s) setStartTime(s[0]);
      if (e) setEndTime(e[1]);
    } else {
      const r = unitToRange(startUnit);
      if (r) {
        setStartTime(r[0]);
        setEndTime(r[1]);
      }
    }
    // 단일↔다중 전환 시점에만 재동기화
  }, [isMultiDay]);

  const handleRangeChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  // 구간 시각(startHour/endHour) 산출 — 전일/반차도 HHMM 구간으로 환산.
  // 복합기간이면 startHour=시작일 시작시각, endHour=종료일 종료시각.
  const usageParams = useMemo(() => {
    // 반차 환산: 오전 = dayStart~halfBoundary, 오후 = halfBoundary~dayEnd
    const startHour = (() => {
      if (isMultiDay) {
        if (startUnit === 'PM_HALF') return toHHMM(halfBoundary);
        if (startUnit === 'HOUR') return toHHMM(startTime);
        return toHHMM(dayStart);
      }
      if (startUnit === 'AM_HALF') return toHHMM(dayStart);
      if (startUnit === 'PM_HALF') return toHHMM(halfBoundary);
      if (startUnit === 'HOUR') return toHHMM(startTime);
      return toHHMM(dayStart);
    })();
    const endHour = (() => {
      if (isMultiDay) {
        if (endUnit === 'AM_HALF') return toHHMM(halfBoundary);
        if (endUnit === 'HOUR') return toHHMM(endTime);
        return toHHMM(dayEnd);
      }
      if (startUnit === 'AM_HALF') return toHHMM(halfBoundary);
      if (startUnit === 'PM_HALF') return toHHMM(dayEnd);
      if (startUnit === 'HOUR') return toHHMM(endTime);
      return toHHMM(dayEnd);
    })();
    return { startDate, endDate, startHour, endHour };
  }, [
    isMultiDay,
    startUnit,
    endUnit,
    startTime,
    endTime,
    startDate,
    endDate,
    dayStart,
    dayEnd,
    halfBoundary,
  ]);

  // 사용 일수 계산 — /annual-leave/usage API 호출 (HRIS 환산). 1일 = 8시간.
  useEffect(() => {
    if (!open) return;
    const { startDate, endDate, startHour, endHour } = usageParams;
    // 단일일 시간차에서 종료 ≤ 시작이면 무효 (API 호출 생략)
    if (
      !isMultiDay &&
      startUnit === 'HOUR' &&
      Number(endHour) <= Number(startHour)
    ) {
      setUsedDays(0);
      setIsCalculating(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setIsCalculating(true);
    const timer = setTimeout(() => {
      const query = new URLSearchParams({
        startDate,
        endDate,
        startHour,
        endHour,
      }).toString();
      fetchApi<{ data: { usedDays: number } }>({
        method: 'GET',
        url: `/api/v1/passport/me/annual-leave/usage?${query}`,
        signal: controller.signal,
      })
        .then((res) => {
          if (!cancelled) setUsedDays(res?.data?.usedDays ?? 0);
        })
        .catch(() => {
          if (!cancelled) setUsedDays(0);
        })
        .finally(() => {
          if (!cancelled) setIsCalculating(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [open, usageParams, isMultiDay, startUnit]);

  // 시간차 옵션 (근무시간 범위 내)
  // 점심시간(12:00~13:00)은 시작 시각 12:00 / 종료 시각 13:00 선택 불가
  const singleBeginOptions = hourOptions.filter(
    (t) => timeToMin(t) < timeToMin(dayEnd) && timeToMin(t) !== LUNCH_START_MIN,
  );
  const singleEndOptions = hourOptions.filter(
    (t) =>
      timeToMin(t) > timeToMin(startTime) && timeToMin(t) !== LUNCH_END_MIN,
  );
  const startDayBeginOptions = hourOptions.filter(
    (t) => timeToMin(t) < timeToMin(dayEnd) && timeToMin(t) !== LUNCH_START_MIN,
  );
  const endDayEndOptions = hourOptions.filter(
    (t) => timeToMin(t) > timeToMin(dayStart) && timeToMin(t) !== LUNCH_END_MIN,
  );

  const isSubmitDisabled =
    isLoading ||
    isCalculating ||
    usedDays <= 0 ||
    (!isMultiDay &&
      startUnit === 'HOUR' &&
      timeToMin(endTime) <= timeToMin(startTime));

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      // 연차 신청 (POST /api/v1/passport/me/annual-leave) — 모든 연차는 시간연차(H)로 등록.
      // FE 는 시작일/종료일 각각의 from~to 시각 슬롯(HHMM, 분 00/30)을 전송하고,
      // BE 가 요청자 근무시간 범위를 검증한 뒤 즉시 자가결재 승인한다. (성공 204)
      const { startDate, endDate, startHour, endHour } = usageParams;
      // 시작일 from = 시작시각 / to = 다중일이면 퇴근시각, 단일일이면 종료시각.
      // 종료일 from = 다중일이면 출근시각, 단일일이면 시작시각 / to = 종료시각.
      await fetchApi({
        method: 'POST',
        url: '/api/v1/passport/me/annual-leave',
        data: {
          startDate,
          endDate,
          startHourFrom: startHour,
          startHourTo: isMultiDay ? toHHMM(dayEnd) : endHour,
          endHourFrom: isMultiDay ? toHHMM(dayStart) : startHour,
          endHourTo: endHour,
        },
      });
      showSuccess({ message: t('연차 신청이 완료되었습니다.') });
      onSuccess?.();
      onClose();
    } catch (e) {
      const message =
        e instanceof AxiosError
          ? e.response?.data?.message ?? t('연차 신청에 실패했습니다.')
          : t('연차 신청에 실패했습니다.');
      showError({ message });
    } finally {
      setIsLoading(false);
    }
  };

  // 휴가 사용 현황 표 (부여/사용/전년 초과 사용/잔여휴가) — summary API 값 사용.
  // 데이터 미수신 시 '-' 표시.
  const { balanceRows, remainingValue } = useMemo(() => {
    if (!summary) {
      return {
        balanceRows: [
          { label: 'MYPAGE.부여', value: '-' },
          { label: 'MYPAGE.사용', value: '-' },
          { label: 'MYPAGE.전년 초과 사용', value: '-' },
        ],
        remainingValue: '-',
      };
    }
    return {
      balanceRows: [
        {
          label: 'MYPAGE.부여',
          value: `+${formatLeaveMinutes(
            durationToMinutes(summary.grantedDuration),
          )}`,
        },
        {
          label: 'MYPAGE.사용',
          value: `-${formatLeaveMinutes(
            durationToMinutes(summary.usedDuration),
          )}`,
        },
        {
          label: 'MYPAGE.전년 초과 사용',
          // 초과 사용 0일이면 '-0일'이 아니라 '0일'로 표시
          value:
            durationToMinutes(summary.overageDuration) > 0
              ? `-${formatLeaveMinutes(
                  durationToMinutes(summary.overageDuration),
                )}`
              : formatLeaveMinutes(durationToMinutes(summary.overageDuration)),
        },
      ],
      remainingValue: formatLeaveMinutes(
        durationToMinutes(summary.remainingDuration),
      ),
    };
  }, [summary]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobilePath}
      PaperProps={!isMobilePath ? { sx: approvalDialogPaperSx } : undefined}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: 500,
          backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
          borderBottom: '1px solid var(--dk-line, #E7E9EC)',
          lineHeight: '24px',
        }}
      >
        {t('MYPAGE.연차 신청')}
        <Button
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: '12px',
            top: '8px',
            padding: '4px',
            minWidth: 'auto',
          }}
        >
          <CloseIcon width="24px" height="24px" />
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: '24px 20px',
          // MUI 의 `.MuiDialogTitle-root + .MuiDialogContent-root { padding-top: 0 }`
          // 규칙(우선순위가 높음)을 덮기 위해 강제 지정
          '&.MuiDialogContent-root': { paddingTop: '24px' },
          overflowY: 'auto',
        }}
      >
        {/* 안내 문구 */}
        <Box sx={{ mb: '24px' }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'var(--dk-text-secondary, #888D96)',
              lineHeight: '20px',
            }}
          >
            {t(
              '근태 품의는 별도의 결재 단계 없이 즉시 승인되며, 팀장을 포함한 소속 팀원에게 자동 공유됩니다.',
            )}
            <br />
            {t(
              '원활한 업무 공유를 위해 팀장 및 동료에게는 사전 공유하실 것을 권장 드립니다.',
            )}
          </Typography>
          <Typography
            sx={{
              mt: '16px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--dk-danger, #F55060)',
              lineHeight: '20px',
            }}
          >
            {t(
              '연차는 사전 신청을 원칙으로 하며, 특별한 사유를 제외하고는 당일 또는 사후 신청은 불가합니다.',
            )}
          </Typography>
          <Typography
            sx={{
              mt: '4px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--dk-danger, #F55060)',
              lineHeight: '20px',
            }}
          >
            {t(
              'MYPAGE.사실과 다르게 신청할 경우, 인사 규정에 따라 불이익이 발생할 수 있습니다.',
            )}
          </Typography>
        </Box>

        {/* 휴가 사용 현황 */}
        <Box sx={{ mb: '24px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              mb: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'var(--dk-text, #000)',
              }}
            >
              {t('휴가 사용 현황')}
            </Typography>
            <Tooltip
              arrow
              placement="right"
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              title={
                <>
                  {t(
                    '* 잔여: (월차・연차 부여일수) – (월차・연차 사용일수 + 전년도 초과사용분)',
                  )}
                  <br />
                  {t(
                    '* 사용일수 계산 시 휴일 및 임시공휴일은 제외, 월차부터 우선 차감',
                  )}
                </>
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#000',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    maxWidth: 480,
                  },
                },
                arrow: { sx: { color: 'var(--dk-text, #000)' } },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <circle
                    cx="10"
                    cy="9.99967"
                    r="6.66667"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.0083 7.0459L10.0083 7.5459C10.2844 7.5459 10.5083 7.32204 10.5083 7.0459L10.0083 7.0459ZM9.99997 7.0459L9.49997 7.0459C9.49997 7.32204 9.72383 7.5459 9.99997 7.5459L9.99997 7.0459ZM9.99997 7.03757L9.99997 6.53757C9.72383 6.53757 9.49997 6.76142 9.49997 7.03757L9.99997 7.03757ZM10.0083 7.03757L10.5083 7.03757C10.5083 6.76142 10.2844 6.53757 10.0083 6.53757L10.0083 7.03757ZM10.0083 7.0459L10.0083 6.5459L9.99997 6.5459L9.99997 7.0459L9.99997 7.5459L10.0083 7.5459L10.0083 7.0459ZM9.99997 7.0459L10.5 7.0459L10.5 7.03757L9.99997 7.03757L9.49997 7.03757L9.49997 7.0459L9.99997 7.0459ZM9.99997 7.03757L9.99997 7.53757L10.0083 7.53757L10.0083 7.03757L10.0083 6.53757L9.99997 6.53757L9.99997 7.03757ZM10.0083 7.03757L9.5083 7.03757L9.5083 7.0459L10.0083 7.0459L10.5083 7.0459L10.5083 7.03757L10.0083 7.03757Z"
                    fill="black"
                  />
                  <path
                    d="M10.0083 10.0088L10.0083 12.9718"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </Tooltip>
          </Box>
          <Box
            sx={{
              border: '1px solid var(--dk-line, #E7E9EC)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            {balanceRows.map((row) => (
              <BalanceRow
                key={row.label}
                label={t(row.label)}
                value={row.value}
                loading={summaryLoading}
              />
            ))}
            <BalanceRow
              label={t('MYPAGE.잔여휴가')}
              value={remainingValue}
              loading={summaryLoading}
              highlight
              last
            />
          </Box>
        </Box>

        {/* 신청 기간 — 회색(#F9FAFB) 섹션이 모달 좌우 끝까지 full-bleed */}
        <Box
          sx={{
            backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
            // DialogContent 의 좌우 20px / 하단 24px 패딩을 상쇄해 모달 끝까지 확장
            mx: '-20px',
            mb: '-24px',
            padding: '24px 16px',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--dk-text, #000)',
              mb: '16px',
            }}
          >
            {t('신청 기간')}
          </Typography>

          {/* datePicker 흰색 카드: 데스크탑(>768px) 캘린더|상세 2단, 모바일 세로 스택 */}
          <Box
            sx={{
              backgroundColor: 'var(--dk-bg, #fff)',
              borderRadius: '8px',
              boxShadow:
                '0px 0px 4px rgba(0, 0, 0, 0.08) inset, 0px 0px 8px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              '@media (min-width:769px)': {
                flexDirection: 'row',
                alignItems: 'stretch',
              },
            }}
          >
            {/* 캘린더 영역 (모바일: 하단 구분선 / 데스크탑: 우측 구분선) */}
            <Box
              sx={{
                flex: 'none',
                borderBottom: '1px solid var(--dk-line, #E7E9EC)',
                '@media (min-width:769px)': {
                  flex: '0 0 50%',
                  minWidth: 0,
                  borderBottom: 'none',
                  borderRight: '1px solid var(--dk-line, #E7E9EC)',
                },
              }}
            >
              <LeaveCalendar
                startDate={startDate}
                endDate={endDate}
                onChange={handleRangeChange}
              />
            </Box>

            {/* 상세 영역 (시작일/종료일 + 사용 일 수) */}
            <Box
              sx={{
                flex: 'none',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                '@media (min-width:769px)': { flex: '0 0 50%', minWidth: 0 },
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
              >
                {!isMultiDay ? (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'var(--dk-text, #000)',
                        mb: '8px',
                      }}
                    >
                      {`${t('MYPAGE.신청일')} : ${formatDateLabel(
                        startDate,
                        i18n.language,
                      )}`}
                    </Typography>
                    <UnitTabs
                      tabs={unitTabs}
                      value={startUnit}
                      onChange={handleSingleUnitChange}
                    />
                    <TimeRange
                      beginValue={startTime}
                      beginOptions={singleBeginOptions}
                      onBeginChange={handleSingleBeginChange}
                      endValue={endTime}
                      endOptions={singleEndOptions}
                      onEndChange={handleSingleEndChange}
                    />
                  </Box>
                ) : (
                  <>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: 'var(--dk-text, #000)',
                          mb: '8px',
                        }}
                      >
                        {`${t('시작일')} : ${formatDateLabel(
                          startDate,
                          i18n.language,
                        )}`}
                      </Typography>
                      <UnitTabs
                        tabs={unitTabs}
                        value={startUnit}
                        onChange={handleMultiStartUnitChange}
                        disabledUnits={['AM_HALF']}
                        // note="오전 반차는 다중일 신청 시 선택 불가합니다."
                      />
                      <TimeRange
                        beginValue={startTime}
                        beginOptions={startDayBeginOptions}
                        onBeginChange={handleMultiBeginChange}
                        endValue={dayEnd}
                        endLocked
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: 'var(--dk-text, #000)',
                          mb: '8px',
                        }}
                      >
                        {`${t('종료일')} : ${formatDateLabel(
                          endDate,
                          i18n.language,
                        )}`}
                      </Typography>
                      <UnitTabs
                        tabs={unitTabs}
                        value={endUnit}
                        onChange={handleMultiEndUnitChange}
                        disabledUnits={['PM_HALF']}
                        // note="연속성 원칙에 따라 오후 반차는 선택 불가합니다."
                      />
                      <TimeRange
                        beginValue={dayStart}
                        beginLocked
                        endValue={endTime}
                        endOptions={endDayEndOptions}
                        onEndChange={handleMultiEndChange}
                      />
                    </Box>
                  </>
                )}
              </Box>

              {/* 사용 일 수 */}
              <Box
                sx={{
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Typography
                  sx={{ fontSize: '14px', color: 'var(--dk-text, #000)' }}
                >
                  {t('사용 일 수')}
                </Typography>
                {isCalculating ? (
                  <Skeleton variant="text" width={56} height={24} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--dk-danger, #F55060)',
                    }}
                  >
                    {formatLeaveDays(usedDays)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={approvalDialogActionsSx}>
        <Button
          variant="outlined"
          size="medium"
          onClick={onClose}
          sx={actionButtonSx}
        >
          {t('닫기')}
        </Button>
        <Button
          variant="contained"
          size="medium"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={{
            ...actionButtonSx,
            backgroundColor: 'var(--dk-inverse-bg, #000)',
            color: 'var(--dk-inverse-text, #fff)',
            '&:hover': { backgroundColor: 'var(--dk-inverse-bg-hover, #222)' },
          }}
        >
          {t('MYPAGE.신청 완료')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BalanceRow = ({
  label,
  value,
  loading,
  highlight,
  last,
}: {
  label: string;
  value: string;
  loading?: boolean;
  highlight?: boolean;
  last?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      borderBottom: last ? 'none' : '1px solid var(--dk-line, #E7E9EC)',
    }}
  >
    <Box
      sx={{
        width: '30%',
        minWidth: 140,
        padding: '8px',
        backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
        borderRight: '1px solid var(--dk-line, #E7E9EC)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{ fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
      >
        {label}
      </Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      {loading ? (
        <Skeleton variant="text" width={72} height={21} />
      ) : (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: highlight ? 600 : 400,
            color: highlight ? POINT : 'var(--dk-text, #000)',
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  </Box>
);

const UnitTabs = ({
  tabs,
  value,
  onChange,
  disabledUnits = [],
  note,
}: {
  tabs: { unit: LeaveUnit; label: string }[];
  value: LeaveUnit;
  onChange: (unit: LeaveUnit) => void;
  disabledUnits?: LeaveUnit[];
  note?: string;
}) => (
  <Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {tabs.map(({ unit, label }) => {
        const disabled = disabledUnits.includes(unit);
        const selected = value === unit;
        return (
          <Box
            key={unit}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onChange(unit)}
            sx={{
              minWidth: '60px',
              height: '36px',
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: 'inherit',
              cursor: disabled ? 'default' : 'pointer',
              border: selected ? 'none' : '1px solid var(--dk-line, #E7E9EC)',
              backgroundColor: selected
                ? 'var(--dk-inverse-bg, #000)'
                : disabled
                ? 'var(--dk-bg-subtle, #F9FAFB)'
                : 'var(--dk-bg, #fff)',
              color: selected
                ? 'var(--dk-inverse-text, #fff)'
                : disabled
                ? 'var(--dk-text-disabled, #D1D8E2)'
                : 'var(--dk-text-secondary, #888D96)',
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
    {note && (
      <Typography
        sx={{
          mt: '6px',
          fontSize: '12px',
          color: 'var(--dk-text-secondary, #888D96)',
        }}
      >
        {note}
      </Typography>
    )}
  </Box>
);

const timeSelectSx = {
  flex: 1,
  '&&': { height: '44px' },
  '& .MuiInputBase-input': { minHeight: 0, paddingRight: '40px !important' },
  '& .MuiSelect-icon': { transform: 'none', top: '50%', marginTop: '-12px' },
};

const TimeRange = ({
  beginValue,
  beginOptions,
  onBeginChange,
  beginLocked,
  endValue,
  endOptions,
  onEndChange,
  endLocked,
}: {
  beginValue: string;
  beginOptions?: string[];
  onBeginChange?: (v: string) => void;
  beginLocked?: boolean;
  endValue: string;
  endOptions?: string[];
  onEndChange?: (v: string) => void;
  endLocked?: boolean;
}) => (
  <Box sx={{ mt: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
    <TimeField
      value={beginValue}
      options={beginOptions}
      onChange={onBeginChange}
      locked={beginLocked}
    />
    <Typography
      sx={{
        color: 'var(--dk-text, #000)',
        height: '44px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      ~
    </Typography>
    <TimeField
      value={endValue}
      options={endOptions}
      onChange={onEndChange}
      locked={endLocked}
    />
  </Box>
);

const TimeField = ({
  value,
  options,
  onChange,
  locked,
}: {
  value: string;
  options?: string[];
  onChange?: (v: string) => void;
  locked?: boolean;
}) => (
  <BaseSelect
    value={value}
    // datepicker.html: 다중일 시 시작/종료 한쪽은 출·퇴근 시간으로 고정(비활성)
    disabled={locked}
    // 반차 분기 시각(예: 12:30)처럼 1시간 단위 옵션에 없는 현재 값도 표시되도록 병합
    options={toOptions(
      locked
        ? [value]
        : [...new Set([...(options ?? []), value])].sort(
            (a, b) => timeToMin(a) - timeToMin(b),
          ),
    )}
    onChange={(e) => onChange?.(e.target.value as string)}
    placeholder="HH:MM"
    IconComponent={ClockIcon}
    sx={{
      ...timeSelectSx,
      // 비활성(locked) 시 ClockIcon(SVG stroke) 색상을 흐리게 표시
      ...(locked && {
        '& .MuiSelect-icon circle, & .MuiSelect-icon path': {
          stroke: 'var(--dk-text-disabled, #D1D8E2)',
        },
      }),
    }}
  />
);

export default AnnualLeaveModal;
